<script setup>
import { ref, onMounted } from 'vue';
import { Head } from '@inertiajs/vue3';
import axios from 'axios';

const props = defineProps({
    stats: Object,
});

// State
const attempts = ref([]);
const loading = ref(true);
const stats = ref({
    today: 0,
    this_week: 0,
    this_month: 0,
    total: 0
});

// Load data
const loadAttempts = async () => {
    loading.value = true;
    try {
        const response = await axios.get('/admin/failed-login/data');
        if (response.data.status === 'success') {
            attempts.value = response.data.data;
        }
    } catch (error) {
        console.error('Error loading attempts:', error);
    } finally {
        loading.value = false;
    }
};

const loadStats = async () => {
    try {
        const response = await axios.get('/admin/failed-login/stats');
        if (response.data.status === 'success') {
            stats.value = response.data.data;
        }
    } catch (error) {
        console.error('Error loading stats:', error);
    }
};

const clearOldLogs = async () => {
    if (!confirm('Hapus semua log yang lebih dari 30 hari?')) return;
    
    try {
        const response = await axios.post('/admin/failed-login/clear-old');
        if (response.data.status === 'success') {
            alert(response.data.message);
            loadAttempts();
            loadStats();
        }
    } catch (error) {
        alert('Terjadi kesalahan saat menghapus log');
    }
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('id-ID');
};

onMounted(() => {
    loadAttempts();
    loadStats();
    
    // Auto refresh every 30 seconds
    setInterval(() => {
        loadAttempts();
        loadStats();
    }, 30000);
});
</script>

<template>
    <Head title="Failed Login Attempts" />

    <div class="container-full">
        <!-- Content Header -->
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div>
                    <h4 class="page-title">Failed Login Attempts</h4>
                    <nav>
                        <ol class="breadcrumb">
                            <li class="breadcrumb-item"><a href="/admin/dashboard"><i class="mdi mdi-home-outline"></i></a></li>
                            <li class="breadcrumb-item active">Security Log</li>
                        </ol>
                    </nav>
                </div>
                <div>
                    <button type="button" class="btn btn-warning btn-sm" @click="clearOldLogs">
                        <i class="fa fa-trash me-5"></i> Hapus Log Lama (>30 hari)
                    </button>
                </div>
            </div>
        </div>

        <!-- Main content -->
        <section class="content">
            <!-- Statistics Cards -->
            <div class="row mb-3">
                <div class="col-lg-3 col-6">
                    <div class="box" style="border-left: 4px solid #dc3545;">
                        <div class="box-body">
                            <div class="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 class="mb-0">Hari Ini</h6>
                                    <h2 class="mb-0 mt-1">{{ stats.today }}</h2>
                                </div>
                                <i class="fa fa-exclamation-triangle fa-3x text-danger"></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-3 col-6">
                    <div class="box" style="border-left: 4px solid #ffc107;">
                        <div class="box-body">
                            <div class="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 class="mb-0">Minggu Ini</h6>
                                    <h2 class="mb-0 mt-1">{{ stats.this_week }}</h2>
                                </div>
                                <i class="fa fa-calendar fa-3x text-warning"></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-3 col-6">
                    <div class="box" style="border-left: 4px solid #17a2b8;">
                        <div class="box-body">
                            <div class="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 class="mb-0">Bulan Ini</h6>
                                    <h2 class="mb-0 mt-1">{{ stats.this_month }}</h2>
                                </div>
                                <i class="fa fa-calendar-check-o fa-3x text-info"></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-3 col-6">
                    <div class="box" style="border-left: 4px solid #6c757d;">
                        <div class="box-body">
                            <div class="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 class="mb-0">Total</h6>
                                    <h2 class="mb-0 mt-1">{{ stats.total }}</h2>
                                </div>
                                <i class="fa fa-database fa-3x text-secondary"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Table -->
            <div class="row">
                <div class="col-12">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title">Log Percobaan Login Gagal</h4>
                        </div>
                        <div class="box-body">
                            <div class="table-responsive">
                                <table class="table table-bordered table-striped">
                                    <thead>
                                        <tr>
                                            <th width="5%">No</th>
                                            <th width="15%">Email</th>
                                            <th width="12%">IP Address</th>
                                            <th width="15%">Waktu</th>
                                            <th width="10%">Browser</th>
                                            <th width="10%">Device</th>
                                            <th width="13%">Alasan</th>
                                            <th width="10%">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="loading">
                                            <td colspan="8" class="text-center p-5">
                                                <div class="spinner-border text-primary"></div>
                                            </td>
                                        </tr>
                                        <tr v-else-if="attempts.length === 0">
                                            <td colspan="8" class="text-center text-muted p-5">
                                                <i class="fa fa-shield fa-3x mb-3 d-block"></i>
                                                Tidak ada percobaan login gagal. Sistem aman!
                                            </td>
                                        </tr>
                                        <tr v-else v-for="(attempt, index) in attempts" :key="attempt.id">
                                            <td>{{ index + 1 }}</td>
                                            <td>{{ attempt.email || 'N/A' }}</td>
                                            <td><code>{{ attempt.ip_address }}</code></td>
                                            <td>
                                                <span class="d-block">{{ formatDate(attempt.attempted_at) }}</span>
                                                <small class="text-muted">{{ formatTime(attempt.attempted_at) }}</small>
                                            </td>
                                            <td>{{ attempt.browser_name || '-' }}</td>
                                            <td>{{ attempt.device_type || '-' }}</td>
                                            <td>
                                                <span v-if="attempt.reason_label" 
                                                    :class="['badge', 'badge-' + attempt.reason_label.class]">
                                                    {{ attempt.reason_label.text }}
                                                </span>
                                            </td>
                                            <td>
                                                <span :class="['badge', attempt.status === 'blocked' ? 'badge-danger' : 'badge-warning']">
                                                    {{ attempt.status === 'blocked' ? 'Blocked' : 'Failed' }}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>
